import { bindActionCreators } from '@reduxjs/toolkit';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { create } from '../../actions/conversation';
import {
  trackLoginModuleClickLoginMessageDealer as _trackLoginModuleClickLoginMessageDealer,
  trackLoginModuleInViewportMessageDealer as _trackLoginModuleInViewportMessageDealer,
  trackConversionEvent,
} from '../../actions/tracking';
import ReCaptcha from '../../components/re-captcha/re-captcha';
import { ExchangeTypes, useDataExchangeStatus } from '../../hooks/exchange-status';
import type { RootState } from '../../reducers';
import { isAuthenticated } from '../../selectors/auth';
import { conversionEvents } from '../../shared/tracking';
import { getRetailerDocument, getSearchDocument } from '../../utils/reselectors';

import Alert from '../../styled-components/components/Alert';
import { Box } from '../../styled-components/components/Box';
import { Button } from '../../styled-components/components/Button';
import { Column, Columns } from '../../styled-components/components/Columns';
import Content from '../../styled-components/components/Content';
import InputCheckbox from '../../styled-components/components/InputCheckbox';
import InputError from '../../styled-components/components/InputError';
import InputHelp from '../../styled-components/components/InputHelp';
import InputLabel from '../../styled-components/components/InputLabel';
import InputText from '../../styled-components/components/InputText';
import InputTextarea from '../../styled-components/components/InputTextarea';
import Loader from '../../styled-components/components/Loader';
import Modal from '../../styled-components/components/Modal';
import { StackLarge, StackMedium } from '../../styled-components/components/Stack';
import Switch from '../../styled-components/components/Switch';
import { Text } from '../../styled-components/components/Text';

import { urls } from '../../helpers';
import { getMarket } from '../../i18n';
import InputGroup from '../../styled-components/components/InputGroup';
import SignInModal from '../../styled-components/structures/Modals/SignInModal';

const validate = (state: State) => {
  const marketCode = getMarket().marketCode;
  const regisstrationNumberLength = marketCode === 'NO' ? 7 : 6;

  if (!state.message || state.message.length < 5) {
    return false;
  }
  if (
    state.hasExchangeCar &&
    (!state.registrationNumber || state.registrationNumber.length !== regisstrationNumberLength)
  ) {
    return false;
  }
  if (state.hasExchangeCar && (!state.mileage || Number.parseInt(state.mileage || '0', 10) < 1)) {
    return false;
  }

  if (!state.captcha) {
    return false;
  }

  return true;
};

const toCamelCase = (input: string) =>
  input.toLowerCase().replace(/-(.)/g, (_, group1) => group1.toUpperCase());

interface State {
  message: string;
  hasExchangeCar: boolean;
  financialAid: boolean;
  insuranceAid: boolean;
  registrationNumber: string;
  mileage: string;
  carDescription: string;
  offers: any[];
  captcha?: string;
  valid: boolean;
  messageError: boolean;
}

type Keys = 'message' | 'registrationNumber' | 'mileage' | 'carDescription';

interface MessageToRetailerModalProps {
  onClose: () => void;
}

const MessageToRetailerModal = ({ onClose }: MessageToRetailerModalProps) => {
  const { t } = useTranslation(['glossary', 'item', 'validation']);
  const dispatch = useDispatch();

  const requestStatus = useSelector<RootState, number>((s) => s.conversations.requestStatus);
  const responseStatus = useSelector<RootState, number>((s) => s.conversations.responseStatus);
  const authenticated = useSelector(isAuthenticated);
  const item = useSelector(getSearchDocument);
  const retailer = useSelector(getRetailerDocument);

  const actions = bindActionCreators(
    {
      create,
      trackConversionEvent,
      trackLoginModuleInViewportMessageDealer: _trackLoginModuleInViewportMessageDealer,
      trackLoginModuleClickLoginMessageDealer: _trackLoginModuleClickLoginMessageDealer,
    },
    dispatch,
  );

  const requestCreate = useDataExchangeStatus(ExchangeTypes.Creating, [
    requestStatus,
    responseStatus,
  ]);

  const [state, setState] = useState<State>({
    message: '',
    hasExchangeCar: false,
    financialAid: false,
    insuranceAid: false,
    registrationNumber: '',
    mileage: '',
    carDescription: '',
    offers: [],
    valid: false,
    messageError: false,
  });

  useEffect(() => {
    document.body.className += ' overflow-hidden';

    return () => {
      document.body.className = document.body.className.replace('overflow-hidden', '');
    };
  }, []);

  useEffect(() => {
    if (state.message.length >= 5) {
      setState((s) => {
        const state = { ...s };
        state.messageError = false;
        return state;
      });
    }
  }, [state.message]);

  const onReCaptchaChange = (captcha: string | null) => {
    const nextState = { ...state };
    nextState.captcha = captcha || undefined;
    nextState.valid = validate(nextState);
    setState(nextState);
  };

  const onCheckboxChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const nextState = { ...state };

    nextState[e.currentTarget.name as 'financialAid' | 'insuranceAid' | 'hasExchangeCar'] =
      e.currentTarget.checked;

    setState(nextState);
  };

  const onChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const nextState = { ...state };
    nextState[e.currentTarget.name as Keys] = e.currentTarget.value;
    nextState.valid = validate(nextState);
    setState(nextState);
  };

  const onMessageBlur = () => {
    if (state.message.length < 5) {
      setState((s) => {
        const state = { ...s };
        state.messageError = true;
        return state;
      });
    }
  };

  const onSend = () => {
    if (retailer && item) {
      const { marketCode } = getMarket();
      const information: { [key: string]: string | boolean | undefined } = {};
      const metaData: { key: string; value: string }[] = [];

      if (state.hasExchangeCar) {
        metaData.push({
          key: 'exchange-car-regnr',
          value: state.registrationNumber,
        });

        metaData.push({
          key: 'exchange-car-odometer-reading-value',
          value: state.mileage,
        });
        metaData.push({
          key: 'exchange-car-odometer-reading-unit',
          value: marketCode === 'NO' ? 'Kilometer' : 'ScandinavianMile',
        });

        metaData.push({
          key: 'exchange-car-info',
          value: state.carDescription,
        });
      }

      metaData.forEach((i) => {
        information[toCamelCase(i.key)] = i.value;
      });

      if (state.financialAid) {
        metaData.push({
          key: 'financial-aid',
          value: 'Jag är intresserad av finansiering',
        });
      }
      information.financialAid = !!state.financialAid;

      if (state.insuranceAid) {
        metaData.push({
          key: 'insurance-aid',
          value: 'Jag är intresserad av försäkring',
        });
      }
      information.insuranceAid = !!state.insuranceAid;

      actions.trackConversionEvent(item, conversionEvents.CONVERSION_MESSAGE_SENT, {
        information,
      });
      actions.create({
        branchId: retailer.id,
        itemId: item.id,
        messageText: state.message,
        metaData,
        captcha: state.captcha,
      });
    }
  };

  if (requestCreate.pending) {
    return (
      <Modal onClose={onClose} heading={t('sendMessageModal.heading', { ns: 'item' })}>
        <Loader />
      </Modal>
    );
  }

  if (requestCreate.successful) {
    return (
      <Modal onClose={onClose} heading={t('sendMessageModal.heading', { ns: 'item' })}>
        <Content>
          <p>{t('sendMessageModal.messageSent', { ns: 'item', name: retailer?.name })}</p>
        </Content>
      </Modal>
    );
  }

  if (authenticated) {
    let alert = null;

    if (requestCreate.failed) {
      alert = (
        <StackLarge>
          <Alert tone="error">{t('sendMessageModal.couldNotSendMessage', { ns: 'item' })}</Alert>
        </StackLarge>
      );
    }

    return (
      <Modal onClose={onClose} heading={t('sendMessageModal.heading', { ns: 'item' })}>
        <StackLarge>
          <Content>
            <p>
              {t('sendMessageModal.description', {
                ns: 'item',
                name: retailer?.name,
                item: item?.title,
              })}
            </p>
          </Content>
        </StackLarge>
        {alert}
        <StackLarge>
          <StackMedium>
            <InputLabel htmlFor="message-textarea">
              {t('sendMessageModal.messageInput.label', { ns: 'item' })}
            </InputLabel>
            <InputTextarea
              name="message"
              placeholder={t('sendMessageModal.messageInput.placeholder', { ns: 'item' })}
              onChange={onChange}
              onBlur={onMessageBlur}
              value={state.message}
              id="message-textarea"
              hasError={state.messageError}
            />
            {state.messageError && (
              <InputError>
                {t('errorValueNeedMoreThanNumberOfCharacters', { ns: 'validation', count: 5 })}
              </InputError>
            )}
          </StackMedium>
          <StackMedium>
            <InputGroup label={t('sendMessageModal.interestedInHeading', { ns: 'item' })}>
              <InputCheckbox
                id="message-financial"
                name="financialAid"
                checked={state.financialAid}
                onChange={onCheckboxChange}
              >
                {t('sendMessageModal.interestedInFinancialAid', { ns: 'item' })}
              </InputCheckbox>
              <InputCheckbox
                id="message-insurance"
                name="insuranceAid"
                checked={state.insuranceAid}
                onChange={onCheckboxChange}
              >
                {t('sendMessageModal.interestedInInsurance', { ns: 'item' })}
              </InputCheckbox>
            </InputGroup>
          </StackMedium>
          <StackMedium>
            <Box>
              <StackMedium>
                <Columns $spacing={2} $valign="center">
                  <Column $grow>
                    <Text $weight="medium">
                      {t('sendMessageModal.tradeInCar.label', { ns: 'item' })}
                    </Text>
                  </Column>
                  <Column $noShrink>
                    <Switch
                      id="message-has-exchange-car"
                      name="hasExchangeCar"
                      checked={state.hasExchangeCar}
                      onChange={onCheckboxChange}
                    />
                  </Column>
                </Columns>
              </StackMedium>
              {state.hasExchangeCar && (
                <>
                  <StackMedium>
                    <InputLabel htmlFor="message-trade-in-car-regnr">
                      {t('registrationNumber', { ns: 'glossary' })}
                    </InputLabel>
                    <InputText
                      type="text"
                      name="registrationNumber"
                      placeholder={t('sendMessageModal.tradeInCar.registrationNumberPlaceholder', {
                        ns: 'item',
                      })}
                      id="message-trade-in-car-regnr"
                      value={state.registrationNumber}
                      onChange={onChange}
                    />
                  </StackMedium>
                  <StackMedium>
                    <InputLabel htmlFor="message-trade-in-car-mileage">
                      {t('odometer', { ns: 'glossary' })}
                    </InputLabel>
                    <InputText
                      name="mileage"
                      type="number"
                      placeholder={t('sendMessageModal.tradeInCar.odometerPlaceholder', {
                        ns: 'item',
                      })}
                      id="message-trade-in-car-mileage"
                      value={state.mileage}
                      onChange={onChange}
                      unit={t('mileageUnit', {
                        ns: 'glossary',
                        count: 2,
                      })}
                    />
                  </StackMedium>
                  <StackMedium>
                    <InputLabel htmlFor="message-trade-in-car-description">
                      {t('sendMessageModal.tradeInCar.descriptionInput.label', { ns: 'item' })}
                    </InputLabel>
                    <InputTextarea
                      name="carDescription"
                      placeholder={t('sendMessageModal.tradeInCar.descriptionInput.placeholder', {
                        ns: 'item',
                      })}
                      onChange={onChange}
                      value={state.carDescription}
                      id="message-trade-in-car-description"
                    />
                    <InputHelp>
                      {t('sendMessageModal.tradeInCar.descriptionInput.help', { ns: 'item' })}
                    </InputHelp>
                  </StackMedium>
                </>
              )}
            </Box>
            <InputHelp>{t('sendMessageModal.tradeInCar.help', { ns: 'item' })}</InputHelp>
          </StackMedium>
          <StackMedium>
            <ReCaptcha onChange={onReCaptchaChange} />
          </StackMedium>
        </StackLarge>
        <StackLarge>
          <Button
            $tone="primary"
            $fullWidth
            disabled={!state.valid}
            onClick={onSend}
            title={t('sendMessageModal.sendMessageButton.title', { ns: 'item' })}
          >
            {t('sendMessageModal.sendMessageButton.text', { ns: 'item' })}
          </Button>
        </StackLarge>
      </Modal>
    );
  }

  return (
    <SignInModal
      onClose={onClose}
      title={t('sendMessageModal.loginHeading', { ns: 'item' })}
      image={urls.cdn('/images/modal/message_retailer.svg')}
      trackOnInit={actions.trackLoginModuleInViewportMessageDealer}
      trackOnSignInClick={actions.trackLoginModuleClickLoginMessageDealer}
    />
  );
};

export default MessageToRetailerModal;
